/**
 * Thin top loading bar for the document workspace.
 *
 * Indeterminate primary bar (YouTube/GitHub style). Driven by document
 * fetch, first surface boot, and brief mode-switch windows — never a
 * full-screen spinner that steals chrome.
 */
import { useEffect, useRef, useState } from "react";
import { testIds } from "@/testIds";

/** Stay visible at least this long so the bar doesn't flash on fast loads. */
const MIN_VISIBLE_MS = 280;
/** After load ends, finish the bar to 100% then fade out. */
const COMPLETE_MS = 220;

export function WorkspaceLoadingBar({ active }: { active: boolean }) {
  const [visible, setVisible] = useState(active);
  const [phase, setPhase] = useState<"idle" | "run" | "done">(active ? "run" : "idle");
  const shownAt = useRef<number | null>(active ? performance.now() : null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (completeTimer.current) clearTimeout(completeTimer.current);
    };
  }, []);

  useEffect(() => {
    if (active) {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      if (completeTimer.current) {
        clearTimeout(completeTimer.current);
        completeTimer.current = null;
      }
      shownAt.current = performance.now();
      setVisible(true);
      setPhase("run");
      return;
    }

    // Finish animation even if load was instant.
    const elapsed = shownAt.current != null ? performance.now() - shownAt.current : MIN_VISIBLE_MS;
    const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);

    hideTimer.current = setTimeout(() => {
      setPhase("done");
      completeTimer.current = setTimeout(() => {
        setVisible(false);
        setPhase("idle");
        shownAt.current = null;
      }, COMPLETE_MS);
    }, wait);

    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [active]);

  if (!visible) return null;

  return (
    <div
      role="progressbar"
      aria-busy={phase === "run"}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={phase === "run" ? "Loading" : "Loaded"}
      data-testid={testIds.chatFirst.workspaceLoadingBar}
      data-phase={phase}
      className="pointer-events-none absolute inset-x-0 top-0 z-[200] h-[2px] overflow-hidden"
    >
      <div
        className={
          phase === "done"
            ? "h-full w-full origin-left bg-[var(--lilia-primary,#0A76A4)] transition-opacity duration-200 opacity-0"
            : "lilia-workspace-loading-bar h-full w-full bg-[var(--lilia-primary,#0A76A4)]"
        }
        style={
          phase === "done"
            ? { transform: "scaleX(1)", opacity: 0 }
            : undefined
        }
      />
    </div>
  );
}
